import { Component, Input, OnChanges, SimpleChanges, ViewChild, AfterViewInit, ChangeDetectorRef, OnInit } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import { NoResultFoundComponent } from '../../../shared/components/no-result-found/no-result-found.component';

@Component({
  selector: 'app-soaproduct-volumes',
  standalone: true,
  imports: [ChartModule, NoResultFoundComponent],
  templateUrl: './soaproduct-volumes.component.html',
  styleUrl: './soaproduct-volumes.component.scss'
})
export class SOAProductVolumesComponent implements OnChanges, AfterViewInit, OnInit {
  @Input() productSoaCount: any;
  @ViewChild('chart') chart: any;

  private readonly MAX_LABEL_LENGTH = 20;
  private limitXAxisPoints = true;
  private maxXAxisPoints = 10;

  data = {
    labels: [],
    datasets: [
      {
        label: '',
        backgroundColor: ['#9D1E65', '#65C9BB', '#651244', '#9D1E65', '#65C9BB', '#651244', '#9D1E65', '#65C9BB'],
        data: []
      }
    ]
  };
  options = {
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: '#651244',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#ffffff',
        borderWidth: 1,
        callbacks: {
          title: (context: any) => {
            const label = context[0].label;
            return label;
          },
          label: (context: any) => {
            return `Count: ${context.parsed.x}`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        min: 0,
        max: 250,
        grid: { color: '#e0e0e0' },
        ticks: {
          stepSize: 50
        }
      },
      y: {
        grid: { display: false },
        ticks: {
          padding: 24,
          callback: (value: any) => {
            const label = this.getLabelForValue(value);
            return this.truncateLabel(label);
          }
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    barPercentage: 1,
    categoryPercentage: 0.4,
    barThickness: 20,
  };

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.updateChartData();
  }

  ngAfterViewInit() {
    if (this.productSoaCount) {
      this.updateChartData();
      this.cdr.detectChanges();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.productSoaCount) {
      this.updateChartData();
      if (this.productSoaCount) {
        this.cdr.detectChanges();
      }
    }
  }

  private truncateLabel(label: string): string {
    if (label.length > this.MAX_LABEL_LENGTH) {
      return `${label.substring(0, this.MAX_LABEL_LENGTH)}...`;
    }
    return label;
  }

  private getLabelForValue(value: any): string {
    if (this.productSoaCount && Array.isArray(this.productSoaCount)) {
      const item = this.productSoaCount[value];
      return item ? item.productName : '';
    }
    return '';
  }

  private updateChartData() {
    if (this.productSoaCount && Array.isArray(this.productSoaCount) && this.productSoaCount.length > 0) {
      let selectedData = this.productSoaCount;

      if (this.limitXAxisPoints && this.productSoaCount.length > this.maxXAxisPoints) {
        const totalPoints = this.productSoaCount.length;
        const step = Math.max(1, Math.floor(totalPoints / this.maxXAxisPoints));
        const selectedIndices = [];

        for (let i = 0; i < totalPoints; i += step) {
          selectedIndices.push(i);
          if (selectedIndices.length >= this.maxXAxisPoints) break;
        }

        if (selectedIndices.length < this.maxXAxisPoints && totalPoints > 0) {
          selectedIndices.push(totalPoints - 1);
        }

        selectedData = selectedIndices.map(index => this.productSoaCount[index]);
      }

      this.data = {
        labels: selectedData.map(item => item.productName),
        datasets: [
          {
            label: 'Product Count',
            backgroundColor: ['#9D1E65', '#65C9BB', '#651244', '#9D1E65', '#65C9BB', '#651244', '#9D1E65', '#65C9BB'],
            data: selectedData.map(item => item.count || 0),
            borderRadius: { topRight: 10, bottomRight: 10, topLeft: 0, bottomLeft: 0 }
          }
        ] as any
      };

      const maxCount = Math.max(...selectedData.map(item => item.count || 0));
      let roundedMax = Math.ceil(maxCount / 10) * 10;
      if (roundedMax === 0) roundedMax = 10;
      const stepSize = roundedMax / 10;
      this.options.scales.x.min = 0;
      this.options.scales.x.max = roundedMax;
      this.options.scales.x.ticks.stepSize = stepSize;
    } else {
      this.data = {
        labels: [],
        datasets: [
          {
            label: 'Product Count',
            backgroundColor: ['#9D1E65', '#65C9BB', '#651244', '#9D1E65', '#65C9BB', '#651244', '#9D1E65', '#65C9BB'],
            data: [],
            borderRadius: { topRight: 10, bottomRight: 10, topLeft: 0, bottomLeft: 0 }
          }
        ] as any
      };
    }
  }

  setXAxisPointLimit(enabled: boolean, maxPoints: number = 10): void {
    this.limitXAxisPoints = enabled;
    this.maxXAxisPoints = maxPoints;
    this.updateChartData();
  }

  toggleXAxisPointLimit(): void {
    this.limitXAxisPoints = !this.limitXAxisPoints;
    this.updateChartData();
  }

  getXAxisPointLimitStatus(): boolean {
    return this.limitXAxisPoints;
  }
}
